/**
 * @module MediaGallery
 * @description Product media gallery controller that syncs carousel slides with zoom-dialog
 * picks and selects photographed variant media without showing swatch artwork.
 * @requires @fc/component
 * @requires @fc/events
 * @customElement gallery-viewer
 */
import { FCElement } from '@fc/component';
import { FCEvents } from '@fc/events';
import { pauseAllMediaWithin, pauseInactiveMediaWithin } from '@fc/media-utils';

const GALLERY_TAG = 'gallery-viewer';
const SECTION_SCOPE_SELECTOR = '.shopify-section, dialog';
const MEDIA_SLOT_SELECTOR = [
  '[data-fit-product-slot="media"]',
  '[data-fit-featured-product-media]',
  '[data-quick-add-modal-pane="media"]',
  '[data-testid="product-details-media"]',
  '.product-details-section__media',
].join(',');

/**
 * @typedef {object} MediaGalleryRefs
 * @property {{ open?: (index: number, event?: Event) => void }} [zoomDialogComponent]
 * @property {{ select?: (index: number, direction?: unknown, options?: { animate?: boolean }) => void }} ['slide-carousel']
 * @property {HTMLElement[]} [media]
 */

/**
 * Product media gallery controller.
 *
 * - Selects variant media only when that media exists in the filtered product gallery
 * - Syncs carousel index when zoom dialog picks a media item
 *
 * @extends {FCElement<MediaGalleryRefs>}
 */
export class MediaGallery extends FCElement {
  /** @type {AbortController | null} */
  #eventsController = null;
  #selectedMediaId = '';
  #pendingOptionMediaId = '';
  #statusFrame = null;
  #paginationFrame = null;
  #editorResizeObserver = null;
  #editorLayoutFrame = null;
  #editorLayoutReleaseFrame = null;
  #editorLayoutWidth = null;
  #editorLayoutMode = '';

  connectedCallback() {
    super.connectedCallback();
    this.#bindEvents();
    this.#setupEditorLayoutSync();
    requestAnimationFrame(() => {
      this.#hydrateSelectedMedia();
      this.#syncActiveMedia();
      this.#syncPaginationWindows();
      this.#announcePendingSelectedMedia();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindEvents();
    this.#teardownEditorLayoutSync();
    if (this.#statusFrame != null) {
      cancelAnimationFrame(this.#statusFrame);
      this.#statusFrame = null;
    }
    if (this.#paginationFrame != null) {
      cancelAnimationFrame(this.#paginationFrame);
      this.#paginationFrame = null;
    }
  }

  #bindEvents() {
    this.#unbindEvents();
    this.#eventsController = new AbortController();

    const signal = this.#eventsController.signal;
    const scope = this.closest(SECTION_SCOPE_SELECTOR);
    if (scope instanceof Element && FCEvents?.variantChange) {
      scope.addEventListener(FCEvents.variantChange, this.#onVariantChange, { signal });
    }
    if (scope instanceof Element && FCEvents?.variantPick) {
      scope.addEventListener(FCEvents.variantPick, this.#onVariantPick, { signal });
    }

    const zoomDialog = this.refs.zoomDialogComponent;
    if (zoomDialog instanceof Element && FCEvents?.zoomPick) {
      zoomDialog.addEventListener(FCEvents.zoomPick, this.#onZoomPick, { signal });
    }

    const carousel = this.carousel;
    if (carousel instanceof Element && FCEvents?.carouselPick) {
      carousel.addEventListener(FCEvents.carouselPick, this.#onCarouselPick, { signal });
    }

    this.addEventListener('focusin', this.#onPaginationFocus, { signal });
    window.addEventListener('resize', this.#onPaginationResize, { signal });

    if (this.#isDesignMode()) {
      document.addEventListener('shopify:section:load', this.#onEditorLayoutEvent, { signal });
      document.addEventListener('shopify:section:select', this.#onEditorLayoutEvent, { signal });
      document.addEventListener('shopify:block:select', this.#onEditorLayoutEvent, { signal });
    }
  }

  #unbindEvents() {
    this.#eventsController?.abort();
    this.#eventsController = null;
  }

  /**
   * @param {import('@fc/events').VariantChangeEvent} event
   */
  #onVariantChange = (event) => {
    const variantMediaId = String(event?.detail?.resource?.featured_media?.id || '').trim();

    this.#pendingOptionMediaId = '';
    delete this.dataset.pendingOptionMediaId;

    if (!variantMediaId) return;

    const htmlPayload = event?.detail?.data?.html;
    if (!htmlPayload) {
      if (this.#selectMediaById(variantMediaId, false)) {
        this.#scheduleGalleryStatus(this.#variantOptionLabel(event));
      }
      return;
    }

    const nextGallery = this.#resolveGalleryFromPayload(htmlPayload);
    if (!(nextGallery instanceof HTMLElement)) return;

    const selectedSlide = nextGallery.querySelector(
      `carousel-slide[slide-id="${CSS.escape(variantMediaId)}"], .gallery-viewer__grid [data-media-id="${CSS.escape(variantMediaId)}"]`,
    );
    if (!(selectedSlide instanceof Element)) return;

    this.#selectedMediaId = variantMediaId;
    nextGallery.dataset.selectedMediaId = variantMediaId;
    nextGallery.dataset.announceSelectedMedia = 'true';
    const optionLabel = this.#variantOptionLabel(event);
    if (optionLabel) nextGallery.dataset.selectedOptionLabel = optionLabel;

    pauseAllMediaWithin(this);
    this.replaceWith(nextGallery);
  };

  #onVariantPick = (event) => {
    const mediaId = String(event?.detail?.resource?.mediaId || '').trim();

    this.#pendingOptionMediaId = mediaId;
    if (mediaId && this.#selectMediaById(mediaId, false)) {
      this.dataset.pendingOptionMediaId = mediaId;
      requestAnimationFrame(() => this.#syncActiveMedia());
    } else {
      this.#pendingOptionMediaId = '';
      delete this.dataset.pendingOptionMediaId;
    }
  };

  #onCarouselPick = (event) => {
    const slide = event?.detail?.slide;
    this.#syncActiveMedia(slide instanceof Element ? slide : null);
    this.#schedulePaginationWindowSync(Number(event?.detail?.index));
  };

  #onPaginationFocus = (event) => {
    const dot = event.target instanceof Element ? event.target.closest('[data-ref="dots[]"]') : null;
    const rail = dot?.closest('.carousel-controls__dots--windowed');
    if (!(dot instanceof HTMLElement) || !(rail instanceof HTMLElement) || !this.contains(rail)) return;

    this.#centerPaginationDot(dot, rail, 'smooth');
  };

  #onPaginationResize = () => {
    this.#schedulePaginationWindowSync();
    if (this.#isDesignMode() && !this.#editorResizeObserver) this.#scheduleEditorLayoutRefresh();
  };

  #onEditorLayoutEvent = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target !== this && !target.contains(this) && !this.contains(target)) return;

    this.#scheduleEditorLayoutRefresh();
  };

  #setupEditorLayoutSync() {
    if (!this.#isDesignMode() || typeof ResizeObserver === 'undefined') return;

    const rect = this.getBoundingClientRect();
    this.#editorLayoutWidth = rect.width > 0 ? Math.round(rect.width * 100) / 100 : null;
    this.#editorLayoutMode = this.#layoutModeForWidth(rect.width);

    this.#editorResizeObserver = new ResizeObserver((entries) => {
      const entry = entries.find(({ target }) => target === this);
      if (!entry) return;

      const width = Math.round(entry.contentRect.width * 100) / 100;
      if (width <= 0) return;

      const mode = this.#layoutModeForWidth(width);
      if (width === this.#editorLayoutWidth && mode === this.#editorLayoutMode) return;

      this.#editorLayoutWidth = width;
      this.#editorLayoutMode = mode;
      this.#scheduleEditorLayoutRefresh();
    });
    this.#editorResizeObserver.observe(this);
  }

  #teardownEditorLayoutSync() {
    this.#editorResizeObserver?.disconnect();
    this.#editorResizeObserver = null;
    this.#editorLayoutWidth = null;
    this.#editorLayoutMode = '';

    if (this.#editorLayoutFrame != null) cancelAnimationFrame(this.#editorLayoutFrame);
    if (this.#editorLayoutReleaseFrame != null) cancelAnimationFrame(this.#editorLayoutReleaseFrame);
    this.#editorLayoutFrame = null;
    this.#editorLayoutReleaseFrame = null;
    this.removeAttribute('data-fit-layout-refresh');
  }

  #scheduleEditorLayoutRefresh = () => {
    if (!this.#isDesignMode() || !this.isConnected) return;

    if (this.#editorLayoutFrame != null) cancelAnimationFrame(this.#editorLayoutFrame);
    if (this.#editorLayoutReleaseFrame != null) cancelAnimationFrame(this.#editorLayoutReleaseFrame);
    this.#editorLayoutReleaseFrame = null;
    this.setAttribute('data-fit-layout-refresh', '');

    this.#editorLayoutFrame = requestAnimationFrame(() => {
      this.#editorLayoutFrame = null;
      this.carousel?.refresh?.();
      this.#syncPaginationWindows();

      this.#editorLayoutReleaseFrame = requestAnimationFrame(() => {
        this.#editorLayoutReleaseFrame = null;
        this.removeAttribute('data-fit-layout-refresh');
      });
    });
  };

  #layoutModeForWidth(width) {
    if (!Number.isFinite(width) || width <= 0) return '';
    return width >= 750 ? 'desktop' : 'mobile';
  }

  #isDesignMode() {
    return window.Shopify?.designMode === true;
  }

  /**
   * @param {import('@fc/events').ZoomPickEvent} event
   */
  #onZoomPick = (event) => {
    const index = Number(event?.detail?.index);
    if (!Number.isInteger(index) || index < 0) return;

    const carousel = this.carousel;
    if (carousel && typeof carousel.select === 'function') {
      carousel.select(index, undefined, { animate: false });
    }
  };

  #hydrateSelectedMedia() {
    const mediaId = String(this.dataset.selectedMediaId || this.#selectedMediaId || '').trim();
    if (!mediaId) return;

    this.#selectedMediaId = mediaId;
    this.#selectMediaById(mediaId, false);
  }

  #fallbackMediaId(gallery = this) {
    if (!(gallery instanceof Element)) return '';

    const fallbackMediaId = String(gallery instanceof HTMLElement ? gallery.dataset.fallbackMediaId || '' : '').trim();
    if (fallbackMediaId) return fallbackMediaId;

    const firstSlide = gallery.querySelector('carousel-slide.product-media-container, .gallery-viewer__grid > .product-media-container');
    return this.#slideMediaId(firstSlide);
  }

  #selectMediaById(mediaId, animate = false) {
    if (!mediaId) return false;

    const slides = this.mediaElements;
    const index = slides.findIndex((slide) => this.#slideMediaId(slide) === String(mediaId));
    if (index < 0) return false;

    this.#selectedMediaId = String(mediaId);
    this.dataset.selectedMediaId = this.#selectedMediaId;

    const carousel = this.carousel;
    if (carousel && typeof carousel.select === 'function') {
      carousel.select(index, undefined, { animate });
    }

    return true;
  }

  #syncActiveMedia(activeSlide = null) {
    const slide = activeSlide instanceof Element ? activeSlide : this.#getCurrentSlide();
    if (!(slide instanceof Element)) {
      pauseAllMediaWithin(this);
      return;
    }

    pauseInactiveMediaWithin(this, slide);
  }

  #schedulePaginationWindowSync(index) {
    if (this.#paginationFrame != null) {
      cancelAnimationFrame(this.#paginationFrame);
    }

    this.#paginationFrame = requestAnimationFrame(() => {
      this.#paginationFrame = null;
      this.#syncPaginationWindows(index);
    });
  }

  #syncPaginationWindows(index) {
    const currentIndex = Number.isInteger(index) && index >= 0 ? index : Number(this.carousel?.current ?? 0);

    for (const rail of this.querySelectorAll('.carousel-controls__dots--windowed')) {
      if (!(rail instanceof HTMLElement) || rail.getClientRects().length === 0) continue;

      const dot = rail.querySelectorAll('[data-ref="dots[]"]')[currentIndex];
      if (dot instanceof HTMLElement) {
        this.#centerPaginationDot(dot, rail, 'auto');
      }
    }
  }

  #centerPaginationDot(dot, rail, behavior) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dotRect = dot.getBoundingClientRect();
    const railRect = rail.getBoundingClientRect();
    const inlineOffset = dotRect.left + dotRect.width / 2 - (railRect.left + railRect.width / 2);

    rail.scrollBy({
      behavior: reducedMotion ? 'auto' : behavior,
      left: inlineOffset,
    });
  }

  #announcePendingSelectedMedia() {
    if (this.dataset.announceSelectedMedia !== 'true') return;

    const optionLabel = this.dataset.selectedOptionLabel || '';
    delete this.dataset.announceSelectedMedia;
    delete this.dataset.selectedOptionLabel;
    this.#scheduleGalleryStatus(optionLabel);
  }

  #scheduleGalleryStatus(optionLabel = '') {
    if (this.#statusFrame != null) {
      cancelAnimationFrame(this.#statusFrame);
    }

    this.#statusFrame = requestAnimationFrame(() => {
      this.#statusFrame = null;
      this.#announceGalleryStatus(optionLabel);
    });
  }

  #announceGalleryStatus(optionLabel = '') {
    const status = this.refs.galleryStatus;
    if (!(status instanceof HTMLElement)) return;

    const base = this.dataset.galleryStatusMessage || '';
    const label = String(optionLabel || '').replace(/\s+/g, ' ').trim();
    const message = label ? `${base}: ${label}` : base;

    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  /**
   * @param {number} mediaIndex
   * @param {Event} [event]
   */
  zoom(mediaIndex, event) {
    if (!Number.isInteger(mediaIndex) || mediaIndex < 0) return;

    const zoomDialog = this.refs.zoomDialogComponent;
    if (zoomDialog && typeof zoomDialog.open === 'function') {
      zoomDialog.open(mediaIndex, event);
    }
  }

  /**
   * @param {Document | DocumentFragment | Element | string} payload
   * @returns {Element | null}
   */
  #resolveGalleryFromPayload(payload) {
    return this.#resolveElementFromPayload(payload, GALLERY_TAG);
  }

  /**
   * @param {Document | DocumentFragment | Element | string} payload
   * @returns {boolean}
   */
  #syncMediaSlotFromPayload(payload) {
    const currentSlot = this.closest(MEDIA_SLOT_SELECTOR);
    if (!(currentSlot instanceof HTMLElement)) return false;

    const freshSlot = this.#resolveFreshMediaSlotFromPayload(payload, currentSlot);
    pauseAllMediaWithin(this);

    if (freshSlot instanceof HTMLElement) {
      currentSlot.replaceWith(freshSlot);
    } else {
      currentSlot.remove();
    }

    return true;
  }

  /**
   * @param {Document | DocumentFragment | Element | string} payload
   * @param {HTMLElement} currentSlot
   * @returns {HTMLElement | null}
   */
  #resolveFreshMediaSlotFromPayload(payload, currentSlot) {
    for (const selector of this.#mediaSlotSelectors(currentSlot)) {
      const slot = this.#resolveElementFromPayload(payload, selector);
      if (slot instanceof HTMLElement) return slot;
    }

    return null;
  }

  /**
   * @param {HTMLElement} slot
   * @returns {string[]}
   */
  #mediaSlotSelectors(slot) {
    const selectors = [];

    if (slot.matches('[data-fit-product-slot="media"]')) selectors.push('[data-fit-product-slot="media"]');
    if (slot.matches('[data-fit-featured-product-media]')) selectors.push('[data-fit-featured-product-media]');
    if (slot.matches('[data-quick-add-modal-pane="media"]')) selectors.push('[data-quick-add-modal-pane="media"]');
    if (slot.matches('[data-testid="product-details-media"]')) selectors.push('[data-testid="product-details-media"]');
    if (slot.matches('.product-details-section__media')) selectors.push('.product-details-section__media');

    return selectors.length ? selectors : [MEDIA_SLOT_SELECTOR];
  }

  /**
   * @param {Document | DocumentFragment | Element | string} payload
   * @param {string} selector
   * @returns {Element | null}
   */
  #resolveElementFromPayload(payload, selector) {
    if (payload instanceof Element) {
      if (payload.matches(selector)) return payload;
      return payload.querySelector(selector);
    }

    if (payload instanceof Document || payload instanceof DocumentFragment) {
      return payload.querySelector(selector);
    }

    if (typeof payload !== 'string') return null;

    const template = document.createElement('template');
    template.innerHTML = payload;
    return template.content.querySelector(selector);
  }

  get carousel() {
    return this.carouselComponent;
  }

  get media() {
    return this.refs.media;
  }

  get carouselComponent() {
    return this.refs['slide-carousel'];
  }

  get mediaElements() {
    return Array.from(this.querySelectorAll('carousel-slide.product-media-container, .gallery-viewer__grid > .product-media-container'));
  }

  get displayMode() {
    return this.presentation === 'slide-carousel' ? 'carousel' : this.presentation;
  }

  get presentation() {
    return this.dataset.presentation ?? '';
  }

  #getCurrentSlide() {
    const carousel = this.carousel;
    if (carousel?.slides?.length) {
      return carousel.slides[carousel.current] ?? carousel.slides[0] ?? null;
    }

    return this.mediaElements[0] ?? null;
  }

  #slideMediaId(slide) {
    if (!(slide instanceof Element)) return '';

    const directId = slide.getAttribute('slide-id');
    if (directId) return directId;

    return slide.querySelector('[data-media-id]')?.getAttribute('data-media-id') ?? '';
  }

  #variantOptionLabel(event) {
    const option = event?.target?.selectedOption;
    if (option instanceof HTMLOptionElement) {
      return option.textContent?.replace(/\s+/g, ' ').trim() || option.value || '';
    }

    if (option instanceof HTMLInputElement) {
      const inlineLabel = option.closest('label')?.textContent;
      return inlineLabel?.replace(/\s+/g, ' ').trim() || option.value || '';
    }

    return event?.detail?.resource?.title || '';
  }
}

if (!customElements.get(GALLERY_TAG)) {
  customElements.define(GALLERY_TAG, MediaGallery);
}
